/**
 * National park guides — single source of truth for /parques and
 * /parques/:slug (pSEO test, see pseo-plan.md).
 *
 * Same idea as posts.ts: plain typed data, everything downstream (routes,
 * sitemap, page, JSON-LD) is derived from this array.
 *
 * DATA RULES (non-negotiable):
 *  - Every fact comes from the park's official page on
 *    parquesnacionales.gov.co, listed in `fuentes`, checked on `verificadoEl`.
 *  - A fact the official page doesn't state stays out (undefined or []).
 *    The page hides empty sections. Never guess, never fill from memory.
 *  - Paraphrase. The official site is "Todos los derechos reservados".
 *
 * TO ADD A PARK: append one `Parque` object. Nothing else to wire up.
 */

import canoPortada from "@/assets/parques/parque-cano-hero.avif";
import canoDetalle from "@/assets/parques/parque-cano-detalle.avif";
import rosarioPortada from "@/assets/parques/parque-rosario-hero.avif";
import rosarioDetalle from "@/assets/parques/parque-rosario-detalle.avif";
import gorgonaPortada from "@/assets/parques/parque-gorgona-hero.avif";
import gorgonaDetalle from "@/assets/parques/parque-gorgona-detalle.avif";
import cocuyPortada from "@/assets/parques/parque-cocuy-hero.avif";
import cocuyDetalle from "@/assets/parques/parque-cocuy-detalle.avif";
import chingazaPortada from "@/assets/parques/parque-chingaza-hero.avif";
import chingazaDetalle from "@/assets/parques/parque-chingaza-detalle.avif";

export type ParqueCategoria = "PNN" | "SFF" | "VP" | "ANU";

/** As the official site labels the park today. */
export type ParqueEstado = "abierto" | "cerrado" | "restricciones";

export interface ParqueAcceso {
  desde: string;
  como: string;
  tiempo?: string;
}

/** COP, 2026 values. Every park built so far has one price all year. */
export interface ParqueTarifa {
  grupo: string;
  valor: number;
}

/** Color world of the park's panels (DESIGN.md, section 2). */
export type ParqueTema = "macarena" | "rosario" | "gorgona" | "cocuy" | "chingaza";

/** Wikimedia Commons photo. CC BY / BY-SA: author, license and link are shown next to it. */
export interface ParqueFoto {
  src: string;
  width: number;
  height: number;
  alt: string;
  autor: string;
  licencia: string;
  url: string;
}

export interface ParqueFuente {
  label: string;
  url: string;
}

export interface Parque {
  /** URL segment: /parques/<slug>. Kebab-case, no accents. */
  slug: string;
  nombreOficial: string;
  categoria: ParqueCategoria;
  /** What people search (e.g. "Caño Cristales"). Breadcrumb, links, headings. */
  atractivoPrincipal: string;
  /** The page's single H1. Leads with `atractivoPrincipal`. */
  h1: string;
  /** Max 60 characters, used verbatim as <title>. */
  title: string;
  /** Max 155 characters, used verbatim as the meta description. */
  metaDescription: string;
  /** Two or three plain sentences under the H1. Paraphrased. */
  intro: string;
  estado: ParqueEstado;
  /** Short official wording behind `estado`, paraphrased. */
  estadoNota?: string;
  region: string;
  departamentos: string[];
  /** Nearest city to start from, for the at-a-glance card. */
  ciudadCercana?: string;
  accesos: ParqueAcceso[];
  horarios?: string[];
  /** Season or high-season dates, as the official sources state them. */
  temporada?: string;
  tarifas: ParqueTarifa[];
  /** Resolution or note that goes with the fee table. */
  tarifasNota?: string;
  cierres: string[];
  reglas: string[];
  seguro?: string;
  operadores: string[];
  /** Official list of authorized agencies, when we link it instead of copying names. */
  operadoresLista?: ParqueFuente;
  actividades: string[];
  atractivos: string[];
  fuentes: ParqueFuente[];
  /** ISO date, "YYYY-MM-DD". Shown next to the source. */
  verificadoEl: string;
  tema: ParqueTema;
  fotos: { portada: ParqueFoto; detalle: ParqueFoto };
}

export const ESTADO_LABELS: Record<ParqueEstado, string> = {
  abierto: "Abierto",
  cerrado: "Cerrado",
  restricciones: "Abierto con restricciones",
};

const RESOLUCION_551: ParqueFuente = {
    label: "Resolución 551 de 2025: derechos de ingreso 2026 (PDF)",
    url: "https://www.parquesnacionales.gov.co/wp-content/uploads/2026/02/RESOLUCION-551-DE-2025-ACTUALIZA-EL-VALOR-DERECHOS-DE-INGRESO-Y-SERVICIOS-COMPLEMENTARIOS-A-LAS-AREAS-PROTEGIDAS.pdf",
  };

export const parques: Parque[] = [
  {
    slug: "cano-cristales",
    nombreOficial: "Parque Nacional Natural Sierra de la Macarena",
    categoria: "PNN",
    atractivoPrincipal: "Caño Cristales",
    h1: "Caño Cristales: tarifas, cómo llegar y temporada 2026",
    title: "Caño Cristales 2026: tarifas, cómo llegar y temporada",
    metaDescription:
      "Guía de Caño Cristales con datos oficiales: tarifas 2026, cómo llegar a La Macarena, horarios, reglas y fechas de la temporada de colores.",
    intro:
      "Caño Cristales es el río de colores del Parque Nacional Natural Sierra de la Macarena, en el sur del Meta. Solo se visita con una agencia local autorizada y en la temporada de colores. Aquí tienes las tarifas, los accesos y las reglas oficiales en un solo lugar.",
    estado: "abierto",
    estadoNota: "Abierto. Temporada 2026 desde el 29 de mayo hasta diciembre",
    region: "Orinoquía",
    departamentos: ["Meta"],
    ciudadCercana: "La Macarena (Meta)",
    accesos: [
      {
        desde: "Bogotá, Medellín, Cali o Villavicencio",
        como: "En avión hasta La Macarena. Hay vuelos de Satena, Clic o chárter desde Villavicencio. Confirma los cupos del parque antes de comprar el tiquete.",
      },
      {
        desde: "San Vicente del Caguán",
        como: "Por tierra. El tramo hasta La Macarena es de carretera destapada. Se llega desde el interior por Neiva y Florencia.",
        tiempo: "Unas 5 horas",
      },
      {
        desde: "San José del Guaviare",
        como: "Por tierra, por El Capricho y Cachicamo, en carretera en buen estado.",
        tiempo: "Unas 5 horas, según la vía y el clima",
      },
      {
        desde: "La Macarena (pueblo)",
        como: "La agencia operadora local hace el traslado hasta Caño Cristales, Mirador Cristalitos o Raudal de Angosturas.",
      },
    ],
    horarios: [
      "Solicitud del derecho de ingreso: lunes a viernes de 6:00 a. m. a 3:00 p. m.; fines de semana y festivos de 6:00 a. m. a 12:00 m.",
      "Charla de inducción: lunes a viernes de 7:30 a. m. a 12:00 m. y de 2:00 a 4:00 p. m.; fines de semana y festivos de 7:30 a. m. a 12:00 m.",
      "Cristales de Colores y Cristales Selva: salida entre 7:00 y 10:00 a. m., regreso al pueblo hacia las 6:00 p. m.",
      "Mirador Cristalitos: salida entre 7:00 a. m. y 2:00 p. m., regreso a las 5:00 p. m.",
    ],
    temporada:
      "La temporada de colores 2026 abrió el 29 de mayo y va hasta diciembre. Las plantas que dan el color dependen de las lluvias, que van de abril a noviembre.",
    tarifas: [
      { grupo: "Colombiano o residente, de 6 a 25 años", valor: 28000 },
      { grupo: "Colombiano o residente, mayor de 25 años", valor: 51500 },
      { grupo: "Extranjero no residente", valor: 78500 },
    ],
    tarifasNota:
      "Valor único todo el año (Resolución 551 de 2025). El ingreso al parque se paga solo a través de una agencia local. Algunos sitios cobran aparte: Cormacarena (de $23.421 a $75.080) y la Alcaldía de La Macarena ($44.000 colombianos, $66.000 extranjeros). Los residentes de La Macarena no pagan.",
    cierres: [
      "Fuera de la temporada de colores, normalmente desde diciembre. Para 2026 aún no hay fecha de cierre publicada.",
    ],
    reglas: [
      "Guía local autorizado obligatorio: uno por cada 9 personas.",
      "Registro y charla de inducción obligatorios.",
      "Cupos diarios limitados. Reserva con anticipación.",
      "No uses bloqueador, cremas, maquillaje ni repelente antes de entrar al río.",
      "No toques ni arranques la planta que da el color (Macarenia).",
      "No se entra con sandalias ni chanclas.",
      "No se permiten botellas plásticas de un solo uso.",
      "Saca toda tu basura y no toques los petroglifos.",
      "Se recomienda la vacuna contra la fiebre amarilla.",
    ],
    seguro:
      "El ABC oficial 2026 dice que el seguro de accidentes es obligatorio y se compra con la agencia local.",
    operadores: [],
    operadoresLista: {
      label: "Lista oficial de agencias locales autorizadas (ABC 2026, PDF)",
      url: "https://www.parquesnacionales.gov.co/wp-content/uploads/2026/06/ABC-LA-MACARENA-2026-2.pdf",
    },
    actividades: [
      "Senderismo",
      "Baño solo en los sitios autorizados",
      "Observación de aves, fauna y flora",
      "Fotografía no comercial",
    ],
    atractivos: [
      "Caño Cristales: Cristales de Colores y Cristales Selva",
      "Salto del Águila, Los Pianos y Piscina del Turista",
      "Mirador Cristalitos",
      "Raudal de Angosturas I: Ciudad de Piedra y petroglifos",
    ],
    fuentes: [
      {
        label: "PNN Sierra de la Macarena, página oficial",
        url: "https://www.parquesnacionales.gov.co/nuestros-parques/pnn-sierra-de-la-macarena/",
      },
      {
        label: "ABC La Macarena 2026 (PDF)",
        url: "https://www.parquesnacionales.gov.co/wp-content/uploads/2026/06/ABC-LA-MACARENA-2026-2.pdf",
      },
      {
        label: "Inicio de la temporada 2026 en Caño Cristales",
        url: "https://www.parquesnacionales.gov.co/sala-prensa/noticias/inicia-temporada-en-cano-cristales-naturalmente-responsable/",
      },
      RESOLUCION_551,
    ],
    verificadoEl: "2026-10-06",
    tema: "macarena",
    fotos: {
      portada: {
        src: canoPortada,
        width: 1800,
        height: 1192,
        alt: "Cascadas del Manto de la Virgen en Caño Cristales, con el lecho del río rojo y amarillo",
        autor: "Gicaman",
        licencia: "CC BY-SA 4.0",
        url: "https://commons.wikimedia.org/wiki/File:Cascadas_Manto_de_La_Virgen_-_Ca%C3%B1o_Cristales_-_La_Macarena_-_Meta_-_Colombia.jpg",
      },
      detalle: {
        src: canoDetalle,
        width: 1800,
        height: 1192,
        alt: "Pozo Los Ocho en Caño Cristales, con el agua cayendo sobre roca roja",
        autor: "Gicaman",
        licencia: "CC BY-SA 4.0",
        url: "https://commons.wikimedia.org/wiki/File:Los_Ocho_en_Ca%C3%B1o_Cristales.jpg",
      },
    },
  },
  {
    slug: "islas-del-rosario",
    nombreOficial: "Parque Nacional Natural Corales del Rosario y de San Bernardo",
    categoria: "PNN",
    atractivoPrincipal: "Islas del Rosario",
    h1: "Islas del Rosario: tarifas, seguro y cómo llegar",
    title: "Islas del Rosario 2026: tarifa, seguro y cómo llegar",
    metaDescription:
      "Islas del Rosario y San Bernardo con datos oficiales: tarifa 2026, seguro obligatorio, cómo llegar desde Cartagena o Tolú, horarios y reglas.",
    intro:
      "Las Islas del Rosario y el archipiélago de San Bernardo forman el Parque Nacional Natural Corales del Rosario y de San Bernardo, frente a Cartagena. Isla Múcura está en el sector sur, en San Bernardo. Desde marzo de 2026 nadie entra sin el seguro obligatorio.",
    estado: "abierto",
    estadoNota: "Abierto. Seguro obligatorio desde el 1 de marzo de 2026",
    region: "Caribe",
    departamentos: ["Bolívar", "Sucre"],
    ciudadCercana: "Cartagena o Santiago de Tolú",
    accesos: [
      {
        desde: "Cartagena, por mar",
        como: "En lancha desde el muelle turístico La Bodeguita, frente al Parque de La Marina, con un operador autorizado.",
        tiempo: "De 1 a 2 horas, según la embarcación",
      },
      {
        desde: "Cartagena, por tierra",
        como: "Hasta isla Barú y Playa Blanca por el puente cerca de Pasacaballos. Desde Playa Blanca no se puede embarcar hacia otros sectores del parque.",
      },
      {
        desde: "Santiago de Tolú (Sucre)",
        como: "En lancha desde los muelles de la Carrera Primera hacia San Bernardo e Isla Múcura, con un operador autorizado.",
        tiempo: "Cerca de 1 hora",
      },
    ],
    horarios: [
      "Salida de embarcaciones: de 8:00 a 10:00 a. m.",
      "Regreso: según la capitanía de puerto y el estado del mar.",
    ],
    temporada:
      "La temporada alta oficial va del 1 de noviembre al 31 de enero, del 15 de junio al 15 de agosto y en Semana Santa. La tarifa no cambia.",
    tarifas: [
      { grupo: "Islas del Rosario y resto del parque", valor: 13500 },
      { grupo: "Sector San Bernardo", valor: 11000 },
    ],
    tarifasNota:
      "Valor único todo el año. El valor de San Bernardo aparece en la página del parque; el general sale de la Resolución 551 de 2025. El seguro y el transporte se pagan aparte.",
    cierres: [],
    reglas: [
      "Seguro de accidentes obligatorio para todos (Resolución 273 de 2024). Sin seguro no entras.",
      "Registro y charla de inducción obligatorios.",
      "Usa el chaleco salvavidas, ve sentado y no subas a lanchas con sobrecupo.",
      "Buceo y careteo solo en los sitios autorizados.",
      "No pises, toques ni ancles sobre los corales.",
      "No compres ni recojas conchas, estrellas de mar ni coral.",
      "No comas mero guasa, tortuga ni caracol pala.",
      "No se permiten mascotas, música a alto volumen ni plásticos de un solo uso.",
      "Lleva tu documento de identidad. Se recomienda la vacuna contra la fiebre amarilla.",
    ],
    seguro:
      "Obligatorio. Aseguradoras avaladas: Positiva (con Magenta Seguros) y Zurich (con AVIA Seguros). Se paga aparte del ingreso.",
    operadores: [],
    actividades: [
      "Sol y playa",
      "Careteo y buceo",
      "Paseos en lancha",
      "Observación de aves, fauna y flora",
      "Senderismo en tierra",
    ],
    atractivos: [
      "Isla Grande y el archipiélago de Nuestra Señora del Rosario",
      "Archipiélago de San Bernardo: islas Múcura y Tintipán",
      "Playa Blanca y Agua Azul, en isla Barú",
      "Ciénaga de Cholón",
    ],
    fuentes: [
      {
        label: "PNN Corales del Rosario y de San Bernardo, página oficial",
        url: "https://www.parquesnacionales.gov.co/nuestros-parques/pnn-corales-del-rosario-y-de-san-bernardo/",
      },
      {
        label: "Avanza el seguro obligatorio en Corales del Rosario",
        url: "https://www.parquesnacionales.gov.co/sala-prensa/noticias/avanza-seguro-obligatorio-para-turistas-en-corales-del-rosario/",
      },
      {
        label: "Plan de Manejo del parque (PDF)",
        url: "https://www.parquesnacionales.gov.co/wp-content/uploads/2023/06/PLAN-DE-MANEJO-PNNCRYSB.pdf",
      },
      RESOLUCION_551,
    ],
    verificadoEl: "2026-10-06",
    tema: "rosario",
    fotos: {
      portada: {
        src: rosarioPortada,
        width: 1800,
        height: 1195,
        alt: "Muelle de madera y kioscos de palma frente al mar turquesa de Isla Tintipán",
        autor: "Williamzarza",
        licencia: "CC BY-SA 4.0",
        url: "https://commons.wikimedia.org/wiki/File:Paisaje_Caribe%C3%B1o_en_Isla_Tintip%C3%A1n.jpg",
      },
      detalle: {
        src: rosarioDetalle,
        width: 1350,
        height: 1800,
        alt: "Un pelícano flota en el agua clara de Isla Múcura, con lanchas al fondo",
        autor: "Julyparrales",
        licencia: "CC BY-SA 4.0",
        url: "https://commons.wikimedia.org/wiki/File:Pel%C3%ADcano_isla_mucura.jpg",
      },
    },
  },
  {
    slug: "isla-gorgona",
    nombreOficial: "Parque Nacional Natural Gorgona",
    categoria: "PNN",
    atractivoPrincipal: "Isla Gorgona",
    h1: "Isla Gorgona: cómo llegar, tarifas y qué hacer",
    title: "Isla Gorgona 2026: cómo llegar, tarifas y qué hacer",
    metaDescription:
      "Guía de Isla Gorgona con datos oficiales: cómo llegar desde Cali o Buenaventura, tarifas 2026, operadores autorizados, horarios y reglas.",
    intro:
      "Gorgona es una isla del Pacífico colombiano, frente a Guapi (Cauca), y fue cárcel hasta 1984. Hoy es parque nacional y solo se visita en pasadía: en la isla no hay alojamiento ni alimentación. Se llega en lancha con un operador autorizado.",
    estado: "abierto",
    estadoNota: "Abierto solo en pasadía, sin alojamiento ni alimentación en la isla",
    region: "Pacífico",
    departamentos: ["Cauca"],
    ciudadCercana: "Guapi (Cauca)",
    accesos: [
      {
        desde: "Cali",
        como: "Vuelo Cali a Guapi, mototaxi hasta el muelle turístico y lancha rápida a la isla. Las lanchas salen antes de la 1:00 p. m.",
        tiempo: "Vuelo de 40 minutos y lancha de 1 hora y 30 minutos",
      },
      {
        desde: "Buenaventura",
        como: "Lancha rápida a Guapi a las 8:00 a. m., con conexión a Gorgona. Los operadores garantizan la llegada antes de las 2:00 p. m.",
      },
    ],
    horarios: [
      "Salida de lanchas: de 6:30 a 7:30 a. m., sobre todo los fines de semana.",
      "Ingreso al parque: de 8:00 a 10:00 a. m.",
      "Regreso: antes de las 3:00 p. m.",
    ],
    temporada:
      "Época más seca: enero a marzo y julio a septiembre. Las ballenas jorobadas llegan desde julio. En la época de lluvias (abril a junio y octubre a diciembre) se pueden suspender actividades.",
    tarifas: [
      { grupo: "Colombiano o residente, de 6 a 25 años", valor: 19500 },
      { grupo: "Colombiano o residente, mayor de 25 años", valor: 32000 },
      { grupo: "Extranjero no residente", valor: 77500 },
    ],
    tarifasNota:
      "Valor único todo el año (Resolución 551 de 2025). Los nativos o residentes de López de Micay, Timbiquí, Guapi, Iscuandé, El Charco, La Tola, Mosquera y Olaya Herrera no pagan. Las embarcaciones propias pagan tarifas aparte.",
    cierres: [
      "En la época de lluvias se pueden suspender actividades según el mar y los senderos.",
    ],
    reglas: [
      "Reserva con al menos 4 días de anticipación.",
      "Solo se entra con operadores autorizados y con guía.",
      "Charla de inducción obligatoria.",
      "Usa botas de caucho en los senderos.",
      "Las ballenas solo se ven en encuentros casuales, sin perseguirlas.",
      "No se permiten mascotas, alcohol, aerosoles ni plásticos de un solo uso.",
    ],
    seguro: "Se recomienda una póliza de accidentes con una aseguradora aliada de Parques Nacionales.",
    operadores: [
      "Guapitours",
      "Colombia Dive Adventures (barco Ferox)",
      "Barco SeaWolf",
      "Agenda del Mar",
    ],
    actividades: [
      "Buceo y careteo",
      "Senderismo",
      "Avistamiento de ballenas",
      "Baño de mar",
      "Observación de fauna y flora",
    ],
    atractivos: [
      "El Penal, ruinas de la antigua cárcel (395 m, 1 hora)",
      "Ruta Playa Palmeras (600 m, 25 minutos)",
      "Sendero Higuerón (1 km, 1 hora)",
      "Sendero Yundigua (2,4 km, 2 horas)",
      "Vista a la isla Gorgonilla",
    ],
    fuentes: [
      {
        label: "PNN Gorgona, página oficial",
        url: "https://www.parquesnacionales.gov.co/nuestros-parques/pnn-gorgona/",
      },
      {
        label: "Alojamiento y alimentación suspendidos en Gorgona",
        url: "https://www.parquesnacionales.gov.co/sala-prensa/servicios-de-alojamiento-y-alimentacion-suspendidos-en-el-parque-nacional-natural-gorgona/",
      },
      RESOLUCION_551,
    ],
    verificadoEl: "2026-10-06",
    tema: "gorgona",
    fotos: {
      portada: {
        src: gorgonaPortada,
        width: 1800,
        height: 1200,
        alt: "La isla Gorgonilla vista desde la costa rocosa de Gorgona, bajo un cielo azul",
        autor: "LentedeBerilo",
        licencia: "CC BY-SA 4.0",
        url: "https://commons.wikimedia.org/wiki/File:Vistazo_a_la_Isla_Gorgonilla.jpg",
      },
      detalle: {
        src: gorgonaDetalle,
        width: 1800,
        height: 1200,
        alt: "Rana de rayas cafés y blancas sobre fondo oscuro en Isla Gorgona",
        autor: "LentedeBerilo",
        licencia: "CC BY-SA 4.0",
        url: "https://commons.wikimedia.org/wiki/File:Anfibios_de_la_Isla_Gorgona.jpg",
      },
    },
  },
  {
    slug: "nevado-del-cocuy",
    nombreOficial: "Parque Nacional Natural El Cocuy",
    categoria: "PNN",
    atractivoPrincipal: "Nevado del Cocuy",
    h1: "Nevado del Cocuy: senderos, tarifas y cómo llegar",
    title: "Nevado del Cocuy 2026: senderos, tarifas y cómo llegar",
    metaDescription:
      "Guía del Nevado del Cocuy con datos oficiales: los tres senderos, tarifas 2026, cómo llegar desde Bogotá, horarios, cierres y reglas.",
    intro:
      "El Parque Nacional Natural El Cocuy tiene la mayor masa de glaciar de Colombia, en la Sierra Nevada del Cocuy, Güicán y Chita. Se visita por tres senderos de un día, con guía y reserva previa. Se entra desde los pueblos de El Cocuy y Güicán de la Sierra, en Boyacá.",
    estado: "abierto",
    estadoNota: "Abierto. Revisa el estado de vías y puentes antes de viajar",
    region: "Andes",
    departamentos: ["Boyacá", "Arauca", "Casanare"],
    ciudadCercana: "El Cocuy o Güicán de la Sierra (Boyacá)",
    accesos: [
      {
        desde: "Bogotá, en carro",
        como: "Por Tunja, Duitama, Belén y Soatá, todo pavimentado. Desde Soatá hay dos rutas hasta El Cocuy o Güicán, con tramos destapados. No se necesita 4x4.",
      },
      {
        desde: "Bogotá, en bus",
        como: "Buses directos diarios desde la Terminal El Salitre a El Cocuy y a Güicán de la Sierra.",
      },
      {
        desde: "El Cocuy o Güicán",
        como: "Transporte local o expreso hasta el inicio de cada sendero.",
      },
    ],
    horarios: [
      "Oficinas de registro: todos los días de 8:00 a. m. a 12:00 m. y de 1:30 a 4:00 p. m.",
      "Charla de inducción: 8:00 a. m. o 4:00 p. m.",
      "Ingreso a los senderos: entre 6:00 y 8:00 a. m.",
      "Inicio del descenso: a más tardar a la 1:00 p. m. Salida del parque: 5:00 p. m.",
    ],
    tarifas: [
      { grupo: "Colombiano o residente, de 10 a 25 años", valor: 30500 },
      { grupo: "Colombiano o residente, de 25 a 62 años", valor: 52500 },
      { grupo: "Extranjero no residente", valor: 107000 },
      { grupo: "Mayor de 62 años", valor: 0 },
    ],
    tarifasNota:
      "Valor único todo el año (Resolución 551 de 2025). La boleta vale 4 días para los tres senderos. Los menores de 10 años no pueden entrar.",
    cierres: [
      "Siempre cerrados: el glaciar, la Vuelta a la Sierra y el flanco oriental.",
      "Del 10 al 17 de julio de 2026 se cerraron Laguna Grande y Ritacuba Blanco por crecida de ríos. Reabrieron con restricciones en los puentes La Vega y Corralitos.",
    ],
    reglas: [
      "Reserva previa y cupo por sendero. Confirma el plazo con la oficina del parque.",
      "Guía avalado obligatorio.",
      "Registro y charla de inducción obligatorios.",
      "Revisan tu morral: no se permiten plásticos de un solo uso.",
      "No se permite acampar, entrar al glaciar ni bañarse en las lagunas.",
      "No se permiten mascotas, alcohol, fuego ni drones sin permiso.",
    ],
    seguro:
      "La página oficial dice en un apartado que el seguro de accidentes y rescate es obligatorio, y en otro que es recomendado. Tómalo antes de ir.",
    operadores: [],
    actividades: [
      "Senderismo de alta montaña",
      "Observación de aves",
      "Observación de fauna y flora",
      "Fotografía no comercial",
    ],
    atractivos: [
      "Sendero Lagunillas, Púlpito del Diablo (15,6 km ida y vuelta)",
      "Sendero Laguna Grande de la Sierra, hasta el borde del glaciar (19,6 km ida y vuelta)",
      "Sendero Ritacuba Blanco, hasta el borde del glaciar (14 km ida y vuelta)",
    ],
    fuentes: [
      {
        label: "PNN El Cocuy, página oficial",
        url: "https://www.parquesnacionales.gov.co/nuestros-parques/pnn-el-cocuy/",
      },
      {
        label: "Reabren los senderos Laguna Grande y Ritacuba Blanco",
        url: "https://www.parquesnacionales.gov.co/sala-prensa/noticias/reabren-senderos-laguna-grande-y-ritacuba-blanco-en-el-pnn-cocuy/",
      },
      RESOLUCION_551,
    ],
    verificadoEl: "2026-10-06",
    tema: "cocuy",
    fotos: {
      portada: {
        src: cocuyPortada,
        width: 1800,
        height: 1015,
        alt: "Sendero de roca hacia la Laguna Grande de la Sierra, con el glaciar al fondo",
        autor: "Caminante82",
        licencia: "CC BY-SA 4.0",
        url: "https://commons.wikimedia.org/wiki/File:Sendero_a_Laguna_Grande_de_La_Sierra_en_la_Sierra_Nevada_del_Cocuy,_G%C3%BCican_y_Chita.jpg",
      },
      detalle: {
        src: cocuyDetalle,
        width: 1800,
        height: 1350,
        alt: "Frailejones en el páramo con un pico nevado de la Sierra Nevada del Cocuy al fondo",
        autor: "Petruss",
        licencia: "CC BY-SA 3.0",
        url: "https://commons.wikimedia.org/wiki/File:Sierra_Nevada_del_Cocuy_paraje.JPG",
      },
    },
  },
  {
    slug: "chingaza",
    nombreOficial: "Parque Nacional Natural Chingaza",
    categoria: "PNN",
    atractivoPrincipal: "Chingaza",
    h1: "Parque Chingaza: senderos, tarifas y cómo llegar",
    title: "Parque Chingaza 2026: senderos, tarifas y cómo llegar",
    metaDescription:
      "Guía del Parque Nacional Chingaza con datos oficiales: senderos, tarifas 2026, cómo llegar desde La Calera, Fómeque o Guasca y reglas.",
    intro:
      "Chingaza es un parque de páramo y lagunas entre Cundinamarca y el Meta, con entradas por La Calera, Fómeque y Guasca. Se visita con reserva previa, un guía de una agencia avalada y vehículo propio o contratado. No hay transporte público hasta el parque.",
    estado: "abierto",
    estadoNota: "Abierto, con reserva previa",
    region: "Andes",
    departamentos: ["Cundinamarca", "Meta"],
    ciudadCercana: "La Calera, Fómeque o Guasca",
    accesos: [
      {
        desde: "La Calera",
        como: "En vehículo hasta el puesto de control Piedras Gordas y luego al centro Monterredondo.",
        tiempo: "22 km hasta Piedras Gordas y 28 km más hasta Monterredondo",
      },
      {
        desde: "Fómeque",
        como: "En vehículo, que se puede contratar en el pueblo, hasta el puesto La Paila y la Laguna de Chingaza.",
        tiempo: "26 km hasta La Paila",
      },
      {
        desde: "Guasca",
        como: "En vehículo, mejor 4x4, hasta el puesto de control Siecha, inicio del sendero Lagunas de Siecha.",
        tiempo: "15 km",
      },
    ],
    horarios: [
      "Ingreso: de 7:00 a 11:00 a. m. en pasadía; hasta las 11:30 a. m. si te alojas.",
      "Salida: 4:00 p. m.",
      "Reservas por formulario: lunes a viernes de 7:00 a. m. a 5:00 p. m.",
    ],
    tarifas: [
      { grupo: "Colombiano o residente, de 5 a 25 años", valor: 24500 },
      { grupo: "Colombiano o residente, mayor de 25 años", valor: 29000 },
      { grupo: "Extranjero", valor: 78500 },
    ],
    tarifasNota:
      "Valor único todo el año (Resolución 551 de 2025). Los vehículos pagan aparte: automóvil $21.000, buseta $54.000 y bus $113.000. La página del parque y la resolución no coinciden en la edad límite de los adultos; confírmala al reservar.",
    cierres: [],
    reglas: [
      "Reserva aprobada con al menos 3 días de anticipación.",
      "Guía avalado obligatorio, contratado con una agencia autorizada.",
      "Lleva el comprobante de pago impreso y la póliza de accidentes.",
      "Registro y charla de inducción obligatorios.",
      "Solo un sector por día.",
      "No se entra a pie, en bicicleta ni en moto.",
      "Velocidad máxima de 40 km/h. No hay combustible en el parque.",
      "No se permiten mascotas, drones, alcohol ni plásticos de un solo uso.",
    ],
    seguro:
      "La página oficial pide una póliza de accidentes para entrar. Empresas avaladas: Magenta, Colasistencia y Seguros Médicos Internacionales.",
    operadores: ["Corpochingaza: alojamiento, alimentación y camping en el parque"],
    operadoresLista: {
      label: "Lista oficial de agencias avaladas (abril 2026, PDF)",
      url: "https://www.parquesnacionales.gov.co/wp-content/uploads/2026/04/Agencias-PNN-Chingaza.pdf",
    },
    actividades: [
      "Caminatas guiadas",
      "Observación de aves, fauna y flora",
      "Alojamiento y camping en Monterredondo",
      "Fotografía no comercial",
    ],
    atractivos: [
      "Lagunas de Siecha",
      "Laguna de Chingaza, por el sendero Plantas del Camino",
      "Lagunas de Buitrago",
      "Sendero Laguna Seca",
      "Sendero De los Sentidos, accesible",
      "Embalse de Chuza",
    ],
    fuentes: [
      {
        label: "PNN Chingaza, página oficial",
        url: "https://www.parquesnacionales.gov.co/nuestros-parques/pnn-chingaza/",
      },
      {
        label: "Agencias avaladas PNN Chingaza (PDF)",
        url: "https://www.parquesnacionales.gov.co/wp-content/uploads/2026/04/Agencias-PNN-Chingaza.pdf",
      },
      RESOLUCION_551,
    ],
    verificadoEl: "2026-10-06",
    tema: "chingaza",
    fotos: {
      portada: {
        src: chingazaPortada,
        width: 1800,
        height: 1350,
        alt: "Laguna de Chingaza bajo un cielo despejado, rodeada de montañas de páramo",
        autor: "David Ricardo Rodriguez Parra",
        licencia: "CC BY-SA 4.0",
        url: "https://commons.wikimedia.org/wiki/File:LAGUNA_DE_CHINGAZA_2.jpg",
      },
      detalle: {
        src: chingazaDetalle,
        width: 1800,
        height: 1350,
        alt: "Cerros de Chingaza vistos desde la laguna de Ubaque, con nubes sobre las cumbres",
        autor: "Felipe Restrepo Acosta",
        licencia: "CC BY-SA 4.0",
        url: "https://commons.wikimedia.org/wiki/File:2019_Cundinamarca_-_Cerros_de_Chingaza_vistos_desde_la_laguna_de_Ubaque.jpg",
      },
    },
  },
];

export const getParqueBySlug = (slug: string | undefined): Parque | undefined =>
  parques.find((parque) => parque.slug === slug);

export const parquePath = (parque: Pick<Parque, "slug">): string =>
  `/parques/${parque.slug}`;

/**
 * Up to `count` other parks, same region first, then the rest in array
 * order. Computed, so adding a park never means editing the others.
 */
export function relatedParques(parque: Parque, count = 3): Parque[] {
  const others = parques.filter((p) => p.slug !== parque.slug);
  const sameRegion = others.filter((p) => p.region === parque.region);
  const rest = others.filter((p) => p.region !== parque.region);
  return [...sameRegion, ...rest].slice(0, count);
}
